import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  Code as CodeIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon,
  Preview as PreviewIcon,
  Addchart as AddchartIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { format, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

function formatMaybeDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return format(d, 'dd/MM/yyyy HH:mm', { locale: fr });
}

function toISODateLocal(d) {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return format(x, 'yyyy-MM-dd');
}

/**
 * Chaîne SQL / ISO commençant par YYYY-MM-DD → date calendaire locale.
 * Évite new Date('2026-03-24') (UTC minuit) qui affiche la veille en fuseau US, etc.
 */
function formatSourceDate(value) {
  if (value == null || value === '') return '—';
  const str = String(value).trim();
  const isoPrefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (isoPrefix) {
    const y = Number(isoPrefix[1]);
    const m = Number(isoPrefix[2]) - 1;
    const day = Number(isoPrefix[3]);
    const dt = new Date(y, m, day);
    if (!Number.isNaN(dt.getTime())) return format(dt, 'dd/MM/yyyy', { locale: fr });
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str.length > 12 ? str.slice(0, 12) + '…' : str;
  return format(d, 'dd/MM/yyyy', { locale: fr });
}

function EnvoiPeppol() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSendPeppol = user?.role === 'admin' || user?.role === 'superadmin';

  const [factures, setFactures] = useState([]);
  const [selected, setSelected] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [loadingFactures, setLoadingFactures] = useState(true);
  const [loadingHistorique, setLoadingHistorique] = useState(true);
  const [peppolState, setPeppolState] = useState({ loading: false, result: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [maisonsRepos, setMaisonsRepos] = useState([]);
  const [maisonGlobale, setMaisonGlobale] = useState(null);
  const [dateDebut, setDateDebut] = useState(() => startOfDay(new Date()));
  const [dateFin, setDateFin] = useState(() => startOfDay(new Date()));
  const [previewGlobale, setPreviewGlobale] = useState(null);
  const [loadingPreviewGlobale, setLoadingPreviewGlobale] = useState(false);
  const [notesGlobale, setNotesGlobale] = useState('');
  const [creatingGlobale, setCreatingGlobale] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadFactures = useCallback(async () => {
    setLoadingFactures(true);
    try {
      const { data } = await axios.get('/api/factures?include_peppol_consolidation=1');
      setFactures(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Impossible de charger les factures',
        severity: 'error',
      });
      setFactures([]);
    } finally {
      setLoadingFactures(false);
    }
  }, []);

  const loadHistorique = useCallback(async () => {
    setLoadingHistorique(true);
    try {
      const { data } = await axios.get('/api/peppol/historique', { params: { limit: 300 } });
      setHistorique(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Impossible de charger l’historique PEPPOL',
        severity: 'error',
      });
      setHistorique([]);
    } finally {
      setLoadingHistorique(false);
    }
  }, []);

  const loadMaisonsRepos = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/maisons-repos');
      const arr = Array.isArray(data) ? data : [];
      // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
      const filtered = arr.filter(
        (m) => String(m?.nom || '').trim().toLowerCase() !== 'notre foyer'
      );
      setMaisonsRepos(filtered);
    } catch (e) {
      console.error(e);
      setMaisonsRepos([]);
    }
  }, []);

  useEffect(() => {
    loadFactures();
    loadHistorique();
    loadMaisonsRepos();
  }, [loadFactures, loadHistorique, loadMaisonsRepos]);

  const factureId = selected?.id;

  const runPeppolValidate = async () => {
    if (!factureId) {
      showSnackbar('Sélectionnez une facture', 'warning');
      return;
    }
    setPeppolState({ loading: true, result: null });
    try {
      const { data } = await axios.get(`/api/factures/${factureId}/peppol/validate`);
      setPeppolState({ loading: false, result: data });
      if (data.valid) {
        showSnackbar('Conformité PEPPOL : OK (contrôles applicatifs)', 'success');
      } else if (data.mode === 'grouped') {
        showSnackbar('Complétez les données PEPPOL de l’établissement — voir le détail', 'warning');
      } else {
        showSnackbar('Des champs PEPPOL manquent — voir le détail ci-dessous', 'warning');
      }
    } catch (error) {
      setPeppolState({ loading: false, result: null });
      showSnackbar(error.response?.data?.error || 'Erreur validation PEPPOL', 'error');
    }
  };

  const handleDownloadUbl = async () => {
    if (!factureId) {
      showSnackbar('Sélectionnez une facture', 'warning');
      return;
    }
    try {
      const response = await axios.get(`/api/factures/${factureId}/peppol/ubl.xml`, {
        responseType: 'blob',
        validateStatus: () => true,
      });
      if (response.status !== 200) {
        let msg = 'Erreur génération UBL';
        try {
          const text = await response.data.text();
          const j = JSON.parse(text);
          if (j.errors?.length) {
            msg = j.errors.map((e) => e.message).join(' ; ') || j.error || msg;
          } else if (j.error) msg = j.error;
        } catch {
          /* ignore */
        }
        showSnackbar(msg, 'error');
        return;
      }
      const filename = `facture-${selected?.numero || factureId}.xml`.replace(/[^a-zA-Z0-9._-]/g, '_');
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar('XML UBL téléchargé', 'success');
    } catch (error) {
      console.error(error);
      showSnackbar('Erreur téléchargement UBL', 'error');
    }
  };

  const handleSendPeppol = async () => {
    if (!factureId) {
      showSnackbar('Sélectionnez une facture', 'warning');
      return;
    }
    try {
      const { data } = await axios.post(`/api/factures/${factureId}/peppol/send`, {});
      showSnackbar(data.message || 'Envoyé à l’access point', 'success');
      loadHistorique();
    } catch (error) {
      const d = error.response?.data;
      const msg =
        d?.error ||
        (d?.errors && d.errors.map((e) => e.message).join(' ; ')) ||
        'Erreur envoi access point';
      showSnackbar(msg, 'error');
      loadHistorique();
    }
  };

  const handlePreviewGlobale = async () => {
    const nom = maisonGlobale?.nom?.trim();
    const dd = toISODateLocal(dateDebut);
    const df = toISODateLocal(dateFin);
    if (!nom || !dd || !df) {
      showSnackbar('Choisissez une maison de repos et les deux dates de la période.', 'warning');
      return;
    }
    setLoadingPreviewGlobale(true);
    setPreviewGlobale(null);
    try {
      const { data } = await axios.get('/api/peppol/facture-globale/preview', {
        params: { maison_repos: nom, date_debut: dd, date_fin: df },
      });
      setPreviewGlobale(data);
      if (!data.nb_lignes) {
        showSnackbar('Aucune prestation trouvée sur cette période pour cette maison.', 'warning');
      } else {
        showSnackbar(
          `${data.nb_lignes} ligne(s), ${data.nb_factures_sources} facture(s) source(s) — vérifiez le tableau ci-dessous.`,
          'success'
        );
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Erreur lors de la prévisualisation';
      showSnackbar(msg, 'error');
      setPreviewGlobale(null);
    } finally {
      setLoadingPreviewGlobale(false);
    }
  };

  const handleCreateFactureGlobale = async () => {
    const nom = maisonGlobale?.nom?.trim();
    const dd = toISODateLocal(dateDebut);
    const df = toISODateLocal(dateFin);
    if (!nom || !dd || !df) {
      showSnackbar('Choisissez une maison et une période complètes.', 'warning');
      return;
    }
    if (!previewGlobale?.nb_lignes) {
      showSnackbar('Prévisualisez d’abord les prestations et contrôlez le détail.', 'warning');
      return;
    }
    setCreatingGlobale(true);
    try {
      const { data } = await axios.post('/api/peppol/facture-globale', {
        maison_repos: nom,
        date_debut: dd,
        date_fin: df,
        notes: notesGlobale || undefined,
      });
      showSnackbar(data.message || 'Facture globale créée', 'success');
      await loadFactures();
      const { data: all } = await axios.get('/api/factures?include_peppol_consolidation=1');
      const row = Array.isArray(all) ? all.find((f) => f.id === data.id) : null;
      if (row) {
        setSelected(row);
        setPeppolState({ loading: false, result: null });
      }
      setNotesGlobale('');
    } catch (e) {
      showSnackbar(e.response?.data?.error || 'Création impossible', 'error');
    } finally {
      setCreatingGlobale(false);
    }
  };

  const optionLabel = (f) => {
    if (!f) return '';
    // Facture globale PEPPOL : le numéro suffit (le client en tête est technique, pas le libellé métier)
    if (Number(f.peppol_consolidation) === 1) {
      return f.numero || String(f.id);
    }
    const client = [f.client_prenom, f.client_nom].filter(Boolean).join(' ').trim();
    return `${f.numero || f.id}${client ? ` — ${client}` : ''}`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Envoi PEPPOL
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Regroupez les prestations d’une maison de repos sur une période (facture globale), contrôlez le détail, créez la
        facture puis utilisez les actions PEPPOL. Vous pouvez aussi traiter une facture unitaire. L’historique en bas
        recense les envois enregistrés par le serveur.
      </Typography>

      {!canSendPeppol && (
        <Alert severity="info" sx={{ mb: 2 }}>
          L’envoi vers l’access point et la création de facture globale sont réservés aux administrateurs. Vous pouvez
          prévisualiser les consolidations, consulter l’historique et utiliser la vérification / le téléchargement UBL
          sur une facture sélectionnée.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Facture globale (maison de repos + période)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Les maisons listées correspondent à l’onglet « Maisons de repos ». Les prestations agrégées proviennent des
          factures existantes sur la période (hors autres factures de consolidation PEPPOL). Les dates début / fin sont
          initialisées sur la journée en cours pour inclure les factures du jour sans les ressaisir.
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={maisonsRepos}
              getOptionLabel={(m) => (m?.nom ? String(m.nom) : '')}
              value={maisonGlobale}
              onChange={(_, v) => {
                setMaisonGlobale(v);
                setPreviewGlobale(null);
              }}
              renderInput={(params) => <TextField {...params} label="Maison de repos" />}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label="Date début"
                  value={dateDebut}
                  onChange={(v) => {
                    setDateDebut(v);
                    setPreviewGlobale(null);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="Date fin"
                  value={dateFin}
                  onChange={(v) => {
                    setDateFin(v);
                    setPreviewGlobale(null);
                  }}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <Button
                  variant="outlined"
                  startIcon={loadingPreviewGlobale ? <CircularProgress size={18} /> : <PreviewIcon />}
                  onClick={handlePreviewGlobale}
                  disabled={loadingPreviewGlobale}
                >
                  Prévisualiser les prestations
                </Button>
              </Box>
            </LocalizationProvider>
          </Grid>
          {canSendPeppol && (
            <Grid item xs={12}>
              <TextField
                label="Notes (optionnel, ajoutées sur la facture créée)"
                value={notesGlobale}
                onChange={(e) => setNotesGlobale(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
          )}
        </Grid>

        {previewGlobale && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={previewGlobale.nb_lignes ? 'info' : 'warning'} sx={{ mb: 2 }}>
              <strong>
                {previewGlobale.maison_repos} — du {formatSourceDate(previewGlobale.date_debut)} au{' '}
                {formatSourceDate(previewGlobale.date_fin)}
              </strong>
              <br />
              {previewGlobale.nb_lignes} ligne(s)
              {previewGlobale.nb_residents != null ? ` · ${previewGlobale.nb_residents} résident(s)` : ''} ·{' '}
              {previewGlobale.nb_factures_sources} facture(s) source(s) · Total TTC :{' '}
              {Number(previewGlobale.totaux?.total_ttc || 0).toFixed(2)} € (HT{' '}
              {Number(previewGlobale.totaux?.total_ht || 0).toFixed(2)} € · TVA{' '}
              {Number(previewGlobale.totaux?.total_tva || 0).toFixed(2)} €)
            </Alert>
            {previewGlobale.nb_lignes > 0 && (
              <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
                {Array.isArray(previewGlobale.par_resident) && previewGlobale.par_resident.length > 0 ? (
                  previewGlobale.par_resident.map((grp, gidx) => {
                    const nums = (grp.factures_sources || [])
                      .map((f) => f.numero || `#${f.id}`)
                      .join(', ');
                    const numsShort = nums.length > 140 ? `${nums.slice(0, 137)}…` : nums;
                    return (
                      <Box
                        key={grp.client_id != null ? `r-${grp.client_id}` : `rg-${gidx}`}
                        sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
                      >
                        <Box sx={{ px: 2, py: 1.25, bgcolor: 'grey.100', borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Récapitulatif — {grp.resident || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {grp.nb_factures_sources === 1
                              ? '1 facture source'
                              : `${grp.nb_factures_sources} factures sources regroupées`}
                            {numsShort ? ` : ${numsShort}` : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 600 }}>
                            Sous-total TTC : {Number(grp.totaux?.total_ttc || 0).toFixed(2)} € (HT{' '}
                            {Number(grp.totaux?.total_ht || 0).toFixed(2)} € · TVA{' '}
                            {Number(grp.totaux?.total_tva || 0).toFixed(2)} €)
                          </Typography>
                        </Box>
                        <TableContainer>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date facture source</TableCell>
                                <TableCell>N° facture source</TableCell>
                                <TableCell>Article</TableCell>
                                <TableCell align="right">Qté</TableCell>
                                <TableCell align="right">PU HT</TableCell>
                                <TableCell align="right">TVA %</TableCell>
                                <TableCell align="right">Total TTC ligne</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(grp.lignes || []).map((ligne, idx) => (
                                <TableRow key={`${ligne.source_facture_id}-${idx}`}>
                                  <TableCell>{formatSourceDate(ligne.source_date_facture)}</TableCell>
                                  <TableCell>
                                    {ligne.source_facture_id ? (
                                      <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigate(`/factures/${ligne.source_facture_id}`)}
                                      >
                                        {ligne.source_facture_numero || `#${ligne.source_facture_id}`}
                                      </Link>
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {ligne.article_nom}
                                    {ligne.article_reference ? (
                                      <Typography component="span" variant="caption" display="block" color="text.secondary">
                                        {ligne.article_reference}
                                      </Typography>
                                    ) : null}
                                  </TableCell>
                                  <TableCell align="right">{ligne.quantite}</TableCell>
                                  <TableCell align="right">{Number(ligne.prix_unitaire).toFixed(2)} €</TableCell>
                                  <TableCell align="right">{ligne.tva != null ? `${ligne.tva} %` : '—'}</TableCell>
                                  <TableCell align="right">{Number(ligne.total_ligne).toFixed(2)} €</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })
                ) : (
                  <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date facture source</TableCell>
                          <TableCell>N° facture source</TableCell>
                          <TableCell>Résident</TableCell>
                          <TableCell>Article</TableCell>
                          <TableCell align="right">Qté</TableCell>
                          <TableCell align="right">PU HT</TableCell>
                          <TableCell align="right">TVA %</TableCell>
                          <TableCell align="right">Total TTC ligne</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(previewGlobale.lignes || []).map((ligne, idx) => (
                          <TableRow key={`${ligne.source_facture_id}-${idx}`}>
                            <TableCell>{formatSourceDate(ligne.source_date_facture)}</TableCell>
                            <TableCell>
                              {ligne.source_facture_id ? (
                                <Link
                                  component="button"
                                  type="button"
                                  variant="body2"
                                  onClick={() => navigate(`/factures/${ligne.source_facture_id}`)}
                                >
                                  {ligne.source_facture_numero || `#${ligne.source_facture_id}`}
                                </Link>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>{ligne.resident}</TableCell>
                            <TableCell>
                              {ligne.article_nom}
                              {ligne.article_reference ? (
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  {ligne.article_reference}
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">{ligne.quantite}</TableCell>
                            <TableCell align="right">{Number(ligne.prix_unitaire).toFixed(2)} €</TableCell>
                            <TableCell align="right">{ligne.tva != null ? `${ligne.tva} %` : '—'}</TableCell>
                            <TableCell align="right">{Number(ligne.total_ligne).toFixed(2)} €</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
            {canSendPeppol && previewGlobale.nb_lignes > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={creatingGlobale ? <CircularProgress size={20} color="inherit" /> : <AddchartIcon />}
                  onClick={handleCreateFactureGlobale}
                  disabled={creatingGlobale}
                >
                  Créer la facture globale (après contrôle)
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Après création, la nouvelle facture est sélectionnée ci-dessous : vérifiez les champs PEPPOL, puis
                  téléchargez l’UBL ou envoyez à l’access point.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Facture unitaire à traiter
        </Typography>
        {loadingFactures ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Chargement des factures…</Typography>
          </Box>
        ) : (
          <Autocomplete
            options={factures}
            getOptionLabel={optionLabel}
            value={selected}
            onChange={(_, v) => {
              setSelected(v);
              setPeppolState({ loading: false, result: null });
            }}
            renderInput={(params) => <TextField {...params} label="Rechercher une facture" margin="normal" />}
            sx={{ maxWidth: 560, mb: 1 }}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        )}
        {selected && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => navigate(`/factures/${selected.id}`)}
            >
              Ouvrir la fiche facture
            </Button>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FactCheckIcon />}
            onClick={runPeppolValidate}
            disabled={!factureId || peppolState.loading}
          >
            Vérifier les champs
          </Button>
          <Button variant="outlined" startIcon={<CodeIcon />} onClick={handleDownloadUbl} disabled={!factureId}>
            Télécharger XML UBL
          </Button>
          {canSendPeppol && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleSendPeppol}
              disabled={!factureId}
            >
              Envoyer à l’access point
            </Button>
          )}
        </Box>
        {peppolState.result?.peppolGroupedNote && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {peppolState.result.peppolGroupedNote}
          </Alert>
        )}
        {peppolState.result && !peppolState.result.valid && peppolState.result.errors?.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              À compléter pour PEPPOL
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {peppolState.result.errors.map((e, i) => (
                <li key={i}>
                  <Typography variant="body2">{e.message}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
        {peppolState.result?.valid && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Contrôles applicatifs OK — validez tout de même le XML avec votre access point avant production.
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Historique des envois PEPPOL
          </Typography>
          <Button size="small" onClick={loadHistorique} disabled={loadingHistorique}>
            Actualiser
          </Button>
        </Box>
        {loadingHistorique ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Chargement…</Typography>
          </Box>
        ) : historique.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun envoi ou tentative enregistré pour l’instant.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Facture</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Destinataire</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Code HTTP</TableCell>
                  <TableCell>Résumé</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historique.map((row) => {
                  const dest =
                    row.recipient_scheme && row.recipient_id
                      ? `${row.recipient_scheme}:${row.recipient_id}`
                      : '—';
                  const client = [row.client_prenom, row.client_nom].filter(Boolean).join(' ').trim() || '—';
                  const ok = Number(row.success) === 1;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{formatMaybeDate(row.sent_at)}</TableCell>
                      <TableCell>
                        {row.facture_id ? (
                          <Link href={`/factures/${row.facture_id}`} onClick={(e) => { e.preventDefault(); navigate(`/factures/${row.facture_id}`); }}>
                            {row.facture_numero || `#${row.facture_id}`}
                          </Link>
                        ) : (
                          row.facture_numero || '—'
                        )}
                      </TableCell>
                      <TableCell>{client}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: 200 }}>{dest}</TableCell>
                      <TableCell>
                        <Chip size="small" label={ok ? 'Réussi' : 'Échec / refus'} color={ok ? 'success' : 'error'} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.status_code != null ? row.status_code : '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.summary || ''}>
                        {row.summary || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EnvoiPeppol;
